import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import TextButton from '#pplx/frontend/components/buttons/TextButton.js';
import Input from '#pplx/frontend/components/form/Input.js';
import { InputErrorPlacement } from '#pplx/frontend/components/form/InputShared.js';
import Avatar from '#pplx/frontend/components/user/Avatar.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { noop } from 'lodash-es';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
const Username = React.memo(function Username({ showAvatar, showCreatorLabel, showEdit, isCurrentUser = true, textClassName = '', textColor, textVariant = 'smallBold', username: extUsername, image, anon, avatarSize = 'small', isAlwaysEditing = false, onButtonClick = noop, className, }) {
    const { $t } = useIntl();
    const { session, status, update } = useSession();
    const [isEditingUsername, setIsEditingUsername] = React.useState(isAlwaysEditing);
    const [username, setUsername] = React.useState(extUsername ?? (isCurrentUser ? session?.user?.username : 'Anonymous'));
    const [error, setError] = React.useState();
    const { isMobileUserAgent } = useIsMobile();
    const handleEditUsernameMutation = async () => {
        return fetch('/api/user', {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                username,
            }),
        });
    };
    const { mutate: handleEditUsername, isPending } = useMutation({
        mutationFn: handleEditUsernameMutation,
        onSuccess: (res) => {
            res.json().then((data) => {
                if (res.status !== 200) {
                    setError(data.message);
                    return;
                }
                setIsEditingUsername(false);
                setError(undefined);
                update();
                onButtonClick();
            });
        },
    });
    useEffect(() => {
        setUsername(extUsername ?? (isCurrentUser ? session?.user?.username : 'Anonymous'));
    }, [extUsername, isCurrentUser, session?.user?.username]);
    const startEditing = useCallback(() => setIsEditingUsername(true), []);
    const handleEdit = useCallback(() => handleEditUsername(), [handleEditUsername]);
    const cancelEditing = useCallback(() => {
        setUsername(session?.user?.username ?? '');
        if (!isAlwaysEditing) {
            setIsEditingUsername(false);
        }
        onButtonClick();
    }, [isAlwaysEditing, onButtonClick, session?.user?.username]);
    const inlineEditBlock = useMemo(() => {
        return status === 'authenticated' ? (_jsxs("div", { className: "ml-2 mt-2 gap-x-2 flex items-center", children: [_jsx(Button, { variant: "tonal", disabled: isPending, onClick: cancelEditing, children: _jsx(FormattedMessage, { defaultMessage: "Cancel", id: "7cd1cd02d1", description: "Action button label in the Username user profile section" }) }), _jsx(Button, { variant: "primary", disabled: username === session?.user?.username || username === '' || isPending, onClick: handleEdit, children: _jsx(FormattedMessage, { defaultMessage: "Save", id: "7c0fd7af93", description: "Action button label in the Username user profile section" }) })] })) : null;
    }, [cancelEditing, handleEdit, isPending, session, status, username]);
    return (_jsxs("div", { className: classNames('gap-x-1 relative flex items-center', className), children: [showAvatar && (_jsx(Avatar, { size: avatarSize, anon: anon, user: image ?? (isCurrentUser ? (session?.user.image ?? undefined) : undefined) })), isEditingUsername ? (_jsx(Input, { isMobileUserAgent: isMobileUserAgent, value: username, disabled: isPending, errorText: error ??
                    (username === ''
                        ? $t({
                            id: '37dcf380e6',
                            description: 'Error message in the Username user profile section',
                            defaultMessage: 'Username cannot be empty',
                        })
                        : ''), onChange: setUsername, className: "md:min-w-[300px]", inlineEditBlock: inlineEditBlock, errorPlacement: InputErrorPlacement.LABEL, wrapperClassName: "w-full", ariaLabel: $t({
                    defaultMessage: 'Username',
                    description: 'Accessible label for the username input field in the change username modal',
                    id: '72299e58a1',
                }) })) : (_jsx(TextPrimitive, { variant: textVariant, color: textColor, lineClamp: 1, className: classNames('break-all', textClassName), children: anon ? (_jsx(FormattedMessage, { defaultMessage: "Anonymous", id: "81609dba06", description: "Display name for anonymous users" })) : (username) })), showCreatorLabel && (_jsx(BoxPrimitive, { rounded: "full", bg: "subtle", className: "px-2 py-1", children: _jsx(TextPrimitive, { variant: "tiny", color: "light", className: textClassName, children: _jsx(FormattedMessage, { id: "08dee77a01", description: "Label in the Username user profile section", defaultMessage: "Author" }) }) })), showEdit && !isEditingUsername && (_jsx(TextButton, { pill: true, size: "small", icon: $icon('edit'), onClick: startEditing }))] }));
});
export default Username;
//# sourceMappingURL=Username.js.map